<template>
  <div v-if="unit">
    <div class="mt-4 p-2 text-xl">General Admission</div>
    <SMList>
      <SMListItem class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white">
        <SMIcon v-tippy="'Label'" icon="label" />
        <SMInput v-model="label" class="grow" @change="onLabelChange" />
      </SMListItem>
      <SMListItem class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white">
        <SMColorPicker
          v-model="fontColor"
          icon="stroke-color"
          label="Label color"
          class="grow"
          @update:modelValue="updateLabelColor"
        />
      </SMListItem>
      <SMListItem class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white">
        <SMIcon v-tippy="'Font size'" icon="font-size" />
        <SMSlider
          v-model="fontSizeValue"
          :width="200"
          :min="1"
          :max="100"
          :interval="1"
          class="grow"
          @update:modelValue="onFontSizeValueChange"
        />
      </SMListItem>
      <SMListItem class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white">
        <SMIcon v-tippy="'Label rotation'" icon="rotation" />
        <SMSlider
          v-model="rotationValue"
          :width="200"
          :min="-180"
          :max="180"
          :interval="1"
          class="grow"
          @update:modelValue="onRotationValueChange"
        />
      </SMListItem>
      <SMListItem class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white">
        <SMIcon v-tippy="'Label position'" icon="hOffset" />
        <SMSlider
          v-model="hOffsetValue"
          :width="200"
          :min="-300"
          :max="300"
          :interval="1"
          class="grow"
          @drag-end="onDragEnd"
          @update:modelValue="onHOffsetValueChange"
        />
      </SMListItem>
      <SMListItem class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white">
        <SMIcon v-tippy="'Label position'" icon="vOffset" />
        <SMSlider
          v-model="vOffsetValue"
          :width="200"
          :min="-300"
          :max="300"
          :interval="1"
          class="grow"
          @update:modelValue="onVOffsetValueChange"
        />
      </SMListItem>

      <SMListItem class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white">
        <SMIcon v-tippy="'Meta 1'" icon="1" />
        <SMInput :value="meta1" class="grow" @change="onChangeMeta1" />
      </SMListItem>

      <SMListItem class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white">
        <SMIcon v-tippy="'Meta 2'" icon="2" />
        <SMInput :value="meta2" class="grow" @change="onChangeMeta2" />
      </SMListItem>

      <SMListItem
        class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white"
        @click.prevent.stop="duplicate"
      >
        <SMIcon v-tippy="'Duplicate'" icon="copy" />
        <span>Duplicate</span>
      </SMListItem>
      <SMListItem
        class="flex gap-2 items-center border rounded-sm p-2 cursor-pointer bg-white"
        @click.prevent.stop="remove"
      >
        <SMIcon v-tippy="'Delete'" icon="delete" />
        <span>Delete</span>
      </SMListItem>
    </SMList>
  </div>
</template>
<script lang="ts">
// import first from 'lodash/first';
import eventbus from 'tiny-emitter/instance';

import SMColorPicker from '@/components/ui/SMColorPicker.vue';
import SMIcon from '@/components/ui/SMIcon.vue';
import SMInput from '@/components/ui/SMInput.vue';
import SMSlider from '@/components/ui/SMSlider.vue';
import { GA, TEXT } from '@/constants';
import tinycolor from 'tinycolor2';

export default {
  components: {
    SMIcon,
    SMSlider,
    SMInput,
    SMColorPicker,
  },
  props: ['units'],
  data() {
    return {
      label: this.units[0].attributes.label.text || GA.LABEL.text,
      labelColorPicker: false,
      rotationValue: 0,
      hOffsetValue: 0,
      vOffsetValue: 0,
      fontSizeValue: TEXT.SIZE.MEDIUM,
      dragStarted: false,
      fontColor: '#000',
      hOffset: {
        min: 0,
        max: 0,
      },
      vOffset: {
        min: 0,
        max: 0,
      },
    };
  },
  computed: {
    labelColor() {
      return tinycolor(this.fontColor).toRgbString();
    },
    meta1() {
      return this.unit.attributes.meta1;
    },
    meta2() {
      return this.unit.attributes.meta2;
    },
    unit() {
      return this.units[0];
    },
  },
  created() {
    const unit = this.units[0];
    const { text, size, vOffset, hOffset, rotation, stroke, fill } = unit.attributes.label;
    const { minVOffset, maxVOffset, minHOffset, maxHOffset } = unit;
    this.label = text;
    this.fontColor = stroke !== undefined ? stroke : fill;
    this.fontSizeValue = size;
    this.vOffsetValue = vOffset;
    this.hOffsetValue = hOffset;
    this.rotationValue = rotation;
    // offsets for slider
    this.hOffset.min = minHOffset;
    this.hOffset.max = maxHOffset;
    this.vOffset.min = minVOffset;
    this.vOffset.max = maxVOffset;
  },
  methods: {
    onChangeMeta1(value) {
      this.$app.seatmap.triggerUnsavedChanges(true);
      this.unit.changeMeta1(value.trim());
    },
    onChangeMeta2(value) {
      this.$app.seatmap.triggerUnsavedChanges(true);
      this.unit.changeMeta2(value.trim());
    },
    showLabelColorPicker() {
      this.labelColorPicker = true;
    },
    hideLabelColorPicker() {
      this.labelColorPicker = false;
    },
    updateLabelColor(rgbString) {
      this.unit.changeLabelStroke(rgbString);
      this.fontColor = rgbString;
      eventbus.emit('selection:delayedHistory', `${this.unit.type} label color change`);
    },
    onHOffsetValueChange(value) {
      this.unit.changeHOffset(value);
      this.checkDrag();
      eventbus.emit('selection:delayedHistory', `${this.unit.type} label horizontal reposition`);
    },
    onVOffsetValueChange(value) {
      this.unit.changeVOffset(value);
      this.checkDrag();
      eventbus.emit('selection:delayedHistory', `${this.unit.type} label vertical reposition`);
    },
    onFontSizeValueChange(value) {
      this.unit.changeFontSize(value);
      this.checkDrag();
      eventbus.emit('selection:delayedHistory', `${this.unit.type} font size change`);
    },
    onRotationValueChange(value) {
      this.unit.changeRotation(value);
      this.checkDrag();
      eventbus.emit('selection:delayedHistory', `Rotate ${this.unit.type} label`);
    },
    checkDrag() {
      if (!this.dragStarted) {
        this.reinitMenu();
      }
    },
    onInputClick() {
      // intentionally left blank
    },
    onDragStart() {
      this.dragStarted = true;
    },
    onDragEnd() {
      this.dragStarted = false;
      this.reinitMenu();
    },
    reinitMenu() {
      eventbus.emit('menu:reinit');
    },
    onLabelChange(value) {
      this.unit.changeLabel(value.trim());
    },
    duplicate() {
      eventbus.emit('selection:clone');
    },
    remove() {
      eventbus.emit('selection:remove');
    },
  },
};
</script>
